import { Sparkles, Music2, Languages, Palette } from 'lucide-react'
import { bio, bioPersonal, funFacts, interests, stats } from '@/data/site'
import { SectionHeading } from '@/components/ui/section-heading'
import { SpotlightCard } from '@/components/ui/spotlight-card'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { Reveal } from '@/components/ui/reveal'

const factIcons = [Languages, Music2, Palette]

export function About() {
  return (
    <section id="about" aria-labelledby="about-title" className="relative py-24 md:py-32">
      <div className="container">
        <SectionHeading
          id="about-title"
          eyebrow="About"
          title={
            <>
              Curious about problems that <span className="font-serif italic text-zinc-300">don&apos;t fit</span> in one
              domain.
            </>
          }
        />

        <div className="grid gap-4 md:grid-cols-12">
          {/* Bio */}
          <Reveal className="md:col-span-8">
            <SpotlightCard className="h-full p-7 md:p-9">
              <div className="space-y-5 text-[17px] leading-relaxed text-zinc-300">
                {bio.map((p) => (
                  <p key={p.slice(0, 24)}>{p}</p>
                ))}
              </div>
            </SpotlightCard>
          </Reveal>

          {/* Interests */}
          <Reveal className="md:col-span-4" delay={0.08}>
            <SpotlightCard className="h-full p-7" contentClassName="flex flex-col" glow="255 179 217">
              <p className="eyebrow mb-1 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-blush" /> PhD research interests
              </p>
              <p className="mb-6 text-sm text-zinc-500">Where I want to go deeper next</p>
              <ul className="mt-auto flex flex-wrap gap-2">
                {interests.map((t) => (
                  <li key={t} className="chip">
                    {t}
                  </li>
                ))}
              </ul>
            </SpotlightCard>
          </Reveal>

          {/* Stats */}
          {stats.map((s, idx) => (
            <Reveal key={s.label} className="md:col-span-6 lg:col-span-3" delay={0.05 * idx}>
              <SpotlightCard className="h-full p-6" glow={idx % 2 ? '135 206 235' : '196 181 253'}>
                <p className="text-4xl font-medium tracking-tight text-white md:text-5xl">
                  <AnimatedNumber value={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-3 text-sm font-medium text-zinc-200">{s.label}</p>
                <p className="mt-1 text-xs text-zinc-500">{s.desc}</p>
              </SpotlightCard>
            </Reveal>
          ))}

          {/* Beyond research */}
          <Reveal className="md:col-span-12" delay={0.05}>
            <SpotlightCard
              className="p-7 md:p-9"
              contentClassName="grid gap-8 md:grid-cols-[1.4fr_1fr]"
              glow="135 206 235"
            >
              <div>
                <p className="eyebrow mb-4">Beyond research</p>
                <p className="text-pretty leading-relaxed text-zinc-400">{bioPersonal}</p>
              </div>
              <ul className="grid content-center gap-3">
                {funFacts.map((f, idx) => {
                  const Icon = factIcons[idx]
                  return (
                    <li
                      key={f.label}
                      className="flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/[0.05]">
                        <Icon className="h-4 w-4 text-zinc-300" />
                      </span>
                      <span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          {f.label}
                        </span>
                        <span className="text-sm text-zinc-200">{f.value}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </SpotlightCard>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
